import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { clearCredentials } from "../authSlice";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return headers;
  },
});

async function baseQueryWithAutoLogout(args, api, extraOptions) {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status === 401) {
    sessionStorage.setItem("session_expired", "1");
    api.dispatch(clearCredentials());
  }
  return result;
}

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: baseQueryWithAutoLogout,
  tagTypes: ["Service", "Spot", "RoadBlock", "User", "Order", "Blog", "Faq", "Page", "DailySlot", "CustomBooking"],
  endpoints: (builder) => ({
    /* ── FAQ ── */
    getFaqs: builder.query({
      query: () => ({ url: "/faq", params: { all: "true" } }),
      providesTags: ["Faq"],
    }),
    createFaq: builder.mutation({
      query: (body) => ({ url: "/faq", method: "POST", body }),
      invalidatesTags: ["Faq"],
    }),
    updateFaq: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/faq/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Faq"],
    }),
    deleteFaq: builder.mutation({
      query: (id) => ({ url: `/faq/${id}`, method: "DELETE" }),
      invalidatesTags: ["Faq"],
    }),

    /* ── Content pages (terms / privacy) ── */
    getPage: builder.query({
      query: (slug) => `/content/pages/${slug}`,
      providesTags: (_, __, slug) => [{ type: "Page", id: slug }],
    }),
    updatePage: builder.mutation({
      query: ({ slug, ...body }) => ({ url: `/content/pages/${slug}`, method: "PATCH", body }),
      invalidatesTags: (_, __, { slug }) => [{ type: "Page", id: slug }],
    }),

    /* ── Blogs ── */
    getAdminBlogs: builder.query({
      query: () => "/admin/blogs",
      providesTags: ["Blog"],
    }),
    createBlog: builder.mutation({
      query: (body) => ({ url: "/admin/blogs", method: "POST", body }),
      invalidatesTags: ["Blog"],
    }),
    updateBlog: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/admin/blogs/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Blog"],
    }),
    deleteBlog: builder.mutation({
      query: (id) => ({ url: `/admin/blogs/${id}`, method: "DELETE" }),
      invalidatesTags: ["Blog"],
    }),

    /* ── Dashboard ── */
    getDashboardStats: builder.query({
      query: () => "/admin/dashboard",
      providesTags: ["Order", "User", "Service"],
    }),

    /* ── Users ── */
    getUsers: builder.query({
      query: () => "/admin/users",
      providesTags: ["User"],
    }),
    deleteUser: builder.mutation({
      query: (id) => ({ url: `/admin/users/${id}`, method: "DELETE" }),
      invalidatesTags: ["User"],
    }),

    /* ── Orders ── */
    getOrders: builder.query({
      query: () => "/admin/orders",
      providesTags: ["Order"],
    }),
    updateOrderStatus: builder.mutation({
      query: ({ id, status, note }) => ({
        url: `/bookings/${id}/status`,
        method: "PATCH",
        body: { status, note },
      }),
      invalidatesTags: ["Order"],
    }),

    /* ── Services ── */
    getServices: builder.query({
      query: () => "/services",
      providesTags: ["Service"],
    }),
    createService: builder.mutation({
      query: (body) => ({
        url: "/admin/services",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Service"],
    }),
    updateService: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/admin/services/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Service"],
    }),
    deleteService: builder.mutation({
      query: (id) => ({
        url: `/admin/services/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Service"],
    }),

    /* ── Image upload ── */
    uploadImages: builder.mutation({
      query: (formData) => ({
        url: "/upload/images",
        method: "POST",
        body: formData,
      }),
    }),

    /* ── Video upload ── */
    uploadVideo: builder.mutation({
      query: (formData) => ({
        url: "/upload/video",
        method: "POST",
        body: formData,
      }),
    }),

    /* ── Daily slot templates ── */
    getDailySlots: builder.query({
      query: () => "/admin/daily-slots",
      providesTags: ["DailySlot"],
    }),
    createDailySlot: builder.mutation({
      query: (body) => ({ url: "/admin/daily-slots", method: "POST", body }),
      invalidatesTags: ["DailySlot"],
    }),
    updateDailySlot: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/admin/daily-slots/${id}`, method: "PATCH", body }),
      invalidatesTags: ["DailySlot"],
    }),
    deleteDailySlot: builder.mutation({
      query: (id) => ({ url: `/admin/daily-slots/${id}`, method: "DELETE" }),
      invalidatesTags: ["DailySlot"],
    }),

    /* ── Custom bookings ── */
    getCustomBookings: builder.query({
      query: () => "/custom-bookings",
      providesTags: ["CustomBooking"],
    }),
    getCustomBooking: builder.query({
      query: (id) => `/custom-bookings/${id}`,
      providesTags: (_, __, id) => [{ type: "CustomBooking", id }],
    }),
    adminRespondCustomBooking: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/custom-bookings/${id}/admin-respond`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["CustomBooking"],
    }),

    /* ── Spots ── */
    getSpots: builder.query({
      query: () => "/spots",
      providesTags: ["Spot"],
    }),
    createSpot: builder.mutation({
      query: (body) => ({
        url: "/spots",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Spot"],
    }),
    updateSpot: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/spots/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Spot"],
    }),
    deleteSpot: builder.mutation({
      query: (id) => ({
        url: `/spots/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Spot"],
    }),
    // Drag-to-reorder. Body is the FULL list of spot ids in the new order; the
    // backend sets each spot's `order` to its index. We optimistically reorder
    // the cached list so the drag feels instant, and roll back if it fails.
    reorderSpots: builder.mutation({
      query: (orderedIds) => ({
        url: "/spots/reorder",
        method: "PATCH",
        body: { orderedIds },
      }),
      async onQueryStarted(orderedIds, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          adminApi.util.updateQueryData("getSpots", undefined, (draft) => {
            const list = draft?.data;
            if (!Array.isArray(list)) return;
            const byId = new Map(list.map((s) => [s.id, s]));
            const next = orderedIds.map((id) => byId.get(id)).filter(Boolean);
            // Safety: keep any spot not in the id list at the end.
            list.forEach((s) => {
              if (!orderedIds.includes(s.id)) next.push(s);
            });
            next.forEach((s, i) => {
              s.order = i;
            });
            draft.data = next;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    /* ── Road blocks (informational map markers: closed roads, works, etc.) ── */
    getRoadBlocks: builder.query({
      query: () => ({ url: "/road-blocks", params: { all: "true" } }),
      providesTags: ["RoadBlock"],
    }),
    createRoadBlock: builder.mutation({
      query: (body) => ({ url: "/road-blocks", method: "POST", body }),
      invalidatesTags: ["RoadBlock"],
    }),
    updateRoadBlock: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/road-blocks/${id}`, method: "PATCH", body }),
      invalidatesTags: ["RoadBlock"],
    }),
    deleteRoadBlock: builder.mutation({
      query: (id) => ({ url: `/road-blocks/${id}`, method: "DELETE" }),
      invalidatesTags: ["RoadBlock"],
    }),
  }),
});

export const {
  useGetFaqsQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
  useGetPageQuery,
  useUpdatePageMutation,
  useGetAdminBlogsQuery,
  useCreateBlogMutation,
  useUpdateBlogMutation,
  useDeleteBlogMutation,
  useGetDashboardStatsQuery,
  useGetUsersQuery,
  useDeleteUserMutation,
  useGetOrdersQuery,
  useUpdateOrderStatusMutation,
  useGetServicesQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
  useUploadImagesMutation,
  useUploadVideoMutation,
  useGetSpotsQuery,
  useCreateSpotMutation,
  useUpdateSpotMutation,
  useDeleteSpotMutation,
  useReorderSpotsMutation,
  useGetRoadBlocksQuery,
  useCreateRoadBlockMutation,
  useUpdateRoadBlockMutation,
  useDeleteRoadBlockMutation,
  useGetDailySlotsQuery,
  useCreateDailySlotMutation,
  useUpdateDailySlotMutation,
  useDeleteDailySlotMutation,
  useGetCustomBookingsQuery,
  useGetCustomBookingQuery,
  useAdminRespondCustomBookingMutation,
} = adminApi;
